import React, { useState, useEffect } from "react";
import { Box, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import MaterialTable from "../../../Components/MaterialTable";
import { IColumn } from "../../../Components/MaterialTable/MaterialTable";
import { INVITATION_PRICING_PLANS_ENUM } from "../../../store/services/invitations/dto/invitations.dto";
import { ORDER_TYPE, PLAN_NAMES } from "../../../constants";
import { formatCurrency } from "../../../utils/formatCurrency";
import { formatDate } from "../../../utils/formatDate";
import { useLazyGetOrdersQuery } from "../../../store/services/orders/orders.service";
import { Order } from "../../../store/services/orders/dto/orders.dto";
import { INVITATION_ORDER_STATUSES_ENUM } from "../../../store/services/orders/dto/orders.dto";
import { useAppSelector } from "../../../store";
import { getFullOrdersState } from "../../../store/slices/orders/orders.slice";
import OrdersCardsList from "./OrdersCardsList";
const STATUS_COLOR: Record<INVITATION_ORDER_STATUSES_ENUM, "success" | "warning" | "info" | "error" | "default"> = {
	[INVITATION_ORDER_STATUSES_ENUM.COMPLETED]: "success",
	[INVITATION_ORDER_STATUSES_ENUM.PENDING]: "warning",
	[INVITATION_ORDER_STATUSES_ENUM.CANCELLED]: "info",
	[INVITATION_ORDER_STATUSES_ENUM.FAILED]: "error"
};

const PLAN_COLOR: Record<INVITATION_PRICING_PLANS_ENUM, "default" | "primary" | "secondary"> = {
	[INVITATION_PRICING_PLANS_ENUM.FREE]: "default",
	[INVITATION_PRICING_PLANS_ENUM.ESSENTIAL]: "primary",
	[INVITATION_PRICING_PLANS_ENUM.SIGNATURE]: "secondary"
};

const columns: IColumn<Order>[] = [
	{
		label: "Order ID",
		dataKey: "orderId",
		Cell: ({ rowData }) => <Typography fontSize={13}>{rowData.orderId}</Typography>
	},
	{
		label: "Invitation ID",
		dataKey: "invitation_id",
		Cell: ({ rowData }) => (
			<Link
				to={`/manageWeddingInvitation/${rowData.invitation_id}`}
				style={{ fontSize: 13, color: "#1976d2", textDecoration: "none", fontWeight: 500 }}
			>
				{rowData.invitation_id}
			</Link>
		)
	},
	{
		label: "Plan",
		dataKey: "planId",
		Cell: ({ rowData }) => (
			<Chip
				label={PLAN_NAMES[rowData.planId]}
				color={PLAN_COLOR[rowData.planId] ?? "default"}
				size={"small"}
				variant={"outlined"}
			/>
		)
	},
	{
		label: "Status",
		dataKey: "status",
		Cell: ({ rowData }) => (
			<Chip label={ORDER_TYPE[rowData.status]} color={STATUS_COLOR[rowData.status] ?? "default"} size={"small"} />
		)
	},
	{
		label: "Amount",
		dataKey: "amount",
		Cell: ({ rowData }) => (
			<Typography fontSize={13} fontWeight={500}>
				{formatCurrency(rowData.amount, rowData.currency)}
			</Typography>
		)
	},
	{
		label: "Date",
		dataKey: "createdAt",
		Cell: ({ rowData }) => <Typography fontSize={13}>{formatDate(rowData.createdAt, "Africa/Cairo", "PP")}</Typography>
	}
];
const ROWS_PER_PAGE = 10;
const Orders = () => {
	const [page, setPage] = useState(1);
	const theme = useTheme();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [callFetchOrders] = useLazyGetOrdersQuery();
	const { orders, totalItems, loadingOrders } = useAppSelector(getFullOrdersState);

	useEffect(() => {
		callFetchOrders({ page: 1, perPage: 10 });
	}, []);

	const handlePageChange = (page: number) => {
		if (page > 0) {
			setPage(page);
			callFetchOrders({ page, perPage: ROWS_PER_PAGE });
		}
	};

	return (
		<Box>
			{isSmallerScreen ? (
				<OrdersCardsList
					data={orders || []}
					page={page}
					perPage={ROWS_PER_PAGE}
					totalItems={totalItems || 0}
					onPageChange={handlePageChange}
					isLoading={loadingOrders}
				/>
			) : (
				<MaterialTable
					tableLabel={"My Orders"}
					columns={columns}
					data={orders || []}
					page={page}
					rowsPerPage={ROWS_PER_PAGE}
					totalItems={totalItems || 0}
					onPageChange={handlePageChange}
					isLoading={loadingOrders}
				/>
			)}
		</Box>
	);
};

export default Orders;
